import { useQueryClient } from "@tanstack/react-query";
import { CreditFacilityEventLayout } from "features/credit-facility/CreditFacilityEventLayout";
import { CreditFacilitySubpageProps } from "features/credit-facility/CreditFacilitySubpageProps";
import {
  PaydownFundAmountInput,
  PaydownTotalAmountInput,
} from "features/credit-facility/legacy-paydown/CreditFacilityPaydownAmountInputs";
import { useCreditFacilityPaydownCalculator } from "features/credit-facility/legacy-paydown/useCreditFacilityPaydownCalculator";
import { useGetAllocationsByLookupKey } from "features/drawdown/DrawdownUtils";
import {
  CreditFacilityLegacyPaydownCreateMutationBody,
  getCreditFacilityPrincipalUsesRetrieveQueryKey,
  getLegacyCreditFacilityPaydownRetrieveQueryKey,
  useCreditFacilityLegacyPaydownCreate,
  useCreditFacilityRetrieveSuspense,
  useLegacyCreditFacilityPaydownRetrieveSuspense,
} from "gen/endpoints/credit-facility/credit-facility";
import { useFundListSuspense } from "gen/endpoints/fund-entities/fund-entities";
import { EntityFunctionalTypeEnum } from "gen/model/entityFunctionalTypeEnum";
import React, { useCallback } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import Form from "shared/components/form/Form";
import { FormErrorAlert } from "shared/components/form/FormErrorAlert";
import {
  PageWizardFormFooter,
  usePageWizard,
} from "shared/components/layout/PageWizard";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { defaultDataTableRenderCellValue } from "shared/components/table/data/DataTableCellValueRenderer";
import { RenderCellValueFn } from "shared/components/table/data/DataTableTypes";
import { decimalOrZero } from "shared/utils/DecimalUtils";

const renderCellValue: RenderCellValueFn = (props, fieldDisabled, params) => {
  const { cell, columnContext, row } = props;
  if (cell.value?.type !== "money" || !cell.form_field) {
    return defaultDataTableRenderCellValue(props, fieldDisabled, params);
  }
  return columnContext.columnId == "fund_family_amount" ? (
    <PaydownTotalAmountInput
      inputName={cell.form_field.name}
      rowIndex={row.id!}
    />
  ) : (
    <PaydownFundAmountInput
      inputName={cell.form_field.name}
      rowIndex={row.id!}
    />
  );
};

export const CreditFacilityPaydownDetails: React.FC = () => {
  const { navigateNext } = usePageWizard();
  const { t } = useTranslation(["creditFacility", "common"]);
  const queryClient = useQueryClient();
  const { fundFamilyId, creditFacilityId, fundFamilyEvent } =
    useOutletContext<CreditFacilitySubpageProps>();

  const { data: tableData } = useLegacyCreditFacilityPaydownRetrieveSuspense(
    fundFamilyId,
    creditFacilityId,
    fundFamilyEvent.id!,
  );
  const form = useForm<CreditFacilityLegacyPaydownCreateMutationBody>();
  const { data: creditFacility } = useCreditFacilityRetrieveSuspense(
    fundFamilyId,
    creditFacilityId,
  );
  const { data: funds } = useFundListSuspense({
    fund_ids: creditFacility.borrowing_entity_ids ?? [],
    extra_functional_types: [EntityFunctionalTypeEnum.holding_company],
  });
  const watchedFields = form.watch("uses");
  const lookupKeys = watchedFields.map((field) => ({
    transaction_code_id: field.transaction_code_id || null,
    investment_id: field.investment_entity_id || null,
    fund_ids: funds.map((fund) => fund.id!),
  }));
  const getAllocationsByLookupKey = useGetAllocationsByLookupKey(
    fundFamilyId,
    lookupKeys,
    fundFamilyEvent.effective_date,
    funds,
  );

  const {
    mutateAsync: createPaydown,
    error: createError,
    reset: createReset,
  } = useCreditFacilityLegacyPaydownCreate();

  const onSubmit = useCallback(
    async ({ uses }: CreditFacilityLegacyPaydownCreateMutationBody) => {
      const filteredUses = uses.filter(
        (use) =>
          !decimalOrZero(use.fund_family_amount).isZero() &&
          Object.values(use.fund_amounts).every(
            (fund) => !decimalOrZero(fund.amount).isZero(),
          ),
      );
      await createPaydown({
        fundFamilyEventId: fundFamilyEvent.id!,
        fundFamilyId,
        creditFacilityId,
        data: {
          fund_family_event_id: fundFamilyEvent.id,
          effective_date: fundFamilyEvent.effective_date,
          posted_date: fundFamilyEvent.posted_date,
          uses: filteredUses,
        },
      });
      await queryClient.invalidateQueries({
        queryKey: getLegacyCreditFacilityPaydownRetrieveQueryKey(
          fundFamilyId,
          creditFacilityId,
          fundFamilyEvent.id!,
        ),
      });
      await queryClient.invalidateQueries({
        queryKey: getCreditFacilityPrincipalUsesRetrieveQueryKey(
          fundFamilyId,
          creditFacilityId,
          { fund_family_event_id: fundFamilyEvent.id! },
        ),
      });
    },
    [
      createPaydown,
      fundFamilyEvent,
      fundFamilyId,
      creditFacilityId,
      queryClient,
    ],
  );

  useCreditFacilityPaydownCalculator(form, getAllocationsByLookupKey);

  return (
    <CreditFacilityEventLayout canEditEvent={true}>
      <Form
        form={form}
        onSubmit={onSubmit}
        onSuccess={navigateNext}
        onBeforeSubmit={createReset}
        onError={() => true}
        excludeNonFieldErrors={["uses"]}
      >
        <FormErrorAlert error={createError} mb="16px" />
        <TableBox isFullHeight={true} isFullWidth={true}>
          <TableBoxHeader>
            <TableBoxTitle>{t("creditFacility:paydown.type")}</TableBoxTitle>
            <DataTableBoxActions />
          </TableBoxHeader>
          <TableBoxBody>
            <DataTable
              data={tableData}
              renderCellValue={renderCellValue}
              shouldUnregisterFormFields={false}
            />
          </TableBoxBody>
        </TableBox>
        <PageWizardFormFooter />
      </Form>
    </CreditFacilityEventLayout>
  );
};
